// OfflineBanner — subtle top strip when the browser reports offline. The
// service-worker still serves the last-cached roster; this just reassures the
// user that stale data is intentional.
import { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";

export default function OfflineBanner() {
  const { t } = useI18n();
  const [offline, setOffline] = useState(() => typeof navigator !== "undefined" && !navigator.onLine);

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  return (
    <AnimatePresence>
      {offline && (
        <motion.div
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -40, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed top-2 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-2 h-9 px-4 rounded-full bg-[hsl(var(--warning))] text-white text-[12px] font-semibold shadow-[var(--shadow-lg)]"
          data-testid="offline-banner"
          role="status"
        >
          <WifiOff size={13} strokeWidth={2.4} />
          {t("offline_banner") || "You are offline — showing last loaded data"}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
